<template>
  <span class="tag-chip" :class="`size-${size}`" :style="chipStyle">
    <v-icon v-if="isIcon" :size="iconSize" :color="resolvedColor">{{ glyph }}</v-icon>
    <span v-else-if="glyph === 'dot'" class="tag-dot" :style="{ background: resolvedColor }"></span>
    {{ label }}
    <slot name="trailing" />
  </span>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { DEFAULT_MARKER_COLOR } from '@/lib/markerShapeFactory';
import { tagGlyph, TAG_GLYPH_DOT, TAG_GLYPH_NONE } from '@/constants/tagOptions';

const props = defineProps({
  name: { type: String, default: '' },
  color: { type: String, default: null },
  icon: { type: String, default: null },
  placeholder: { type: String, default: 'Tag' },
  size: { type: String, default: 'md' }, // 'sm' (table rows) | 'md' (preview / details)
});

const resolvedColor = computed(() => props.color || DEFAULT_MARKER_COLOR);
const glyph = computed(() => tagGlyph(props.icon));
const isIcon = computed(() => glyph.value !== TAG_GLYPH_NONE && glyph.value !== TAG_GLYPH_DOT);
const label = computed(() => (props.name && props.name.trim()) || props.placeholder);
const iconSize = computed(() => (props.size === 'xs' ? 11 : props.size === 'sm' ? 13 : 15));
const chipStyle = computed(() => ({
  background: `${resolvedColor.value}22`,
  border: `1px solid ${resolvedColor.value}66`,
  color: resolvedColor.value,
}));
</script>

<style scoped>
.tag-chip {
  display: inline-flex; align-items: center; gap: 6px; border-radius: 9999px;
  font-weight: 600; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.tag-chip.size-xs { padding: 2px 7px; font-size: 0.7rem; gap: 4px; }
.tag-chip.size-sm { padding: 3px 10px; font-size: 0.82rem; }
.tag-chip.size-md { padding: 4px 12px; font-size: 0.9rem; }
.tag-dot { flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; }
.tag-chip.size-xs .tag-dot { width: 6px; height: 6px; }
</style>
